/*
  Freakmount product media gallery.

  The main viewer is driven by Embla Carousel (assets/embla-carousel.umd.js) for a
  proper touch-swipe experience with arrows + a "1 / N" counter. Thumbnails,
  variant-image switching, video/3D playback and the zoom modal all keep working —
  Embla only replaces the slider engine.

  Progressive enhancement: if Embla is unavailable, the gallery falls back to Dawn's
  native scroll-snap slider (the <ul> keeps its `slider slider--mobile` classes) and
  thumbnail clicks still switch the active media via native scrolling.
*/
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.emblaRoot = this.querySelector('[data-fm-embla]');
        this.viewport = this.querySelector('[data-fm-embla-viewport]');
        this.mediaList = this.querySelector('.product__media-list');
        if (window.FmPhotoZoom) this.photoZoom = new window.FmPhotoZoom(this);

        // Thumbnail click → activate the matching media.
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        // FreakMount: Embla slider on mobile only; desktop (>=750px) renders a CSS 2-col grid.
        this.syncGalleryMode();
        this.mql.addEventListener('change', () => this.syncGalleryMode());

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Mobile = Embla slider; desktop = static 2-col grid (Embla destroyed).
      syncGalleryMode() {
        if (this.mql.matches) {
          this.photoZoom?.close();
          this.destroyEmbla();
        } else {
          this.initEmbla();
        }
      }

      disconnectedCallback() {
        this.photoZoom?.close();
        this.destroyEmbla();
      }

      destroyEmbla() {
        if (this.embla) {
          this.embla.destroy();
          this.embla = null;
        }
        if (this._onResize) {
          window.removeEventListener('resize', this._onResize);
          this._onResize = null;
        }
        // Drop the per-slide height so the desktop grid isn't constrained by it.
        if (this.viewport) this.viewport.style.height = '';
        if (this.emblaRoot) this.emblaRoot.classList.remove('is-embla');
      }

      initEmbla() {
        if (this.embla) return;
        const slides = this.mediaList ? this.mediaList.querySelectorAll(':scope > li') : [];
        if (!this.viewport || typeof window.EmblaCarousel !== 'function' || slides.length < 2) return;

        // Add is-embla BEFORE creating Embla so our flex / full-width / border-box
        // slide styles are live when Embla measures. Otherwise it measures Dawn's
        // peek layout (content-box 572px slides + a 1.5rem first-slide margin) and
        // bakes misaligned snap points — the "photos not aligned" bug.
        this.emblaRoot.classList.add('is-embla');
        this.embla = window.EmblaCarousel(this.viewport, {
          loop: false,
          align: 'start',
          containScroll: 'trimSnaps',
          // We set the viewport height ourselves per slide (adaptive height). Embla's
          // own ResizeObserver watches that same element, so leaving it on turns every
          // height change into a reInit — which snaps the carousel back to slide 0.
          // Disable it and drive genuine (width) resizes from our own handler below.
          watchResize: false,
        });

        // Real viewport-width changes (e.g. phone rotation) need a reInit + height
        // recalc; rAF-debounced so a burst of resize events collapses to one.
        this._onResize = () => {
          if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
          this._resizeRaf = requestAnimationFrame(() => {
            if (!this.embla) return;
            this.embla.reInit();
            this.setViewportHeight();
          });
        };
        window.addEventListener('resize', this._onResize);

        this.prevArrow = this.emblaRoot.querySelector('[data-fm-embla-prev]');
        this.nextArrow = this.emblaRoot.querySelector('[data-fm-embla-next]');
        if (this.prevArrow) this.prevArrow.addEventListener('click', () => this.embla.scrollPrev());
        if (this.nextArrow) this.nextArrow.addEventListener('click', () => this.embla.scrollNext());

        this.counter = this.emblaRoot.querySelector('[data-fm-embla-counter]');
        this.updateCounter();

        // The click that ends a drag never reaches the zoom button: Embla v8 stops it itself, with
        // its own capture listener. (v7's clickAllowed() is gone in v8 and threw on every tap.)
        this.embla.on('pointerDown', () => this.emblaRoot.classList.add('is-dragging'));
        this.embla.on('pointerUp', () => this.emblaRoot.classList.remove('is-dragging'));

        this.embla.on('select', () => this.onEmblaSelect());
        this.embla.on('reInit', () => {
          this.updateCounter();
          this.updateArrows();
          this.setViewportHeight();
        });

        this.onEmblaSelect();
        this.updateArrows();
        this.setViewportHeight();
      }

      // Rapha-style "1 / 6" overlay — replaces the mobile dots.
      updateCounter() {
        if (!this.counter || !this.embla) return;
        const total = this.embla.scrollSnapList().length;
        const current = this.embla.selectedScrollSnap() + 1;
        this.counter.textContent = `${current} / ${total}`;
      }

      // Aspect ratio of a slide's media. --ratio (set in the Liquid from the
      // media's aspect_ratio) is exact and available before the image loads; fall
      // back to the loaded image's intrinsic size, then to square.
      slideRatio(slide) {
        const container = slide.querySelector('.product-media-container');
        const raw = container ? parseFloat(container.style.getPropertyValue('--ratio')) : 0;
        if (raw && raw > 0) return raw;
        const img = slide.querySelector('img');
        if (img && img.naturalWidth && img.naturalHeight) return img.naturalWidth / img.naturalHeight;
        return 1;
      }

      // Adaptive height: the viewport hugs the active photo (height = width / ratio),
      // so square and portrait photos each fill the frame with no letterbox. Derived
      // from width + intrinsic ratio (never the height it sets), so no feedback loop.
      setViewportHeight() {
        if (!this.embla || !this.viewport) return;
        const active = this.embla.slideNodes()[this.embla.selectedScrollSnap()];
        if (!active) return;
        const width = this.viewport.clientWidth;
        if (!width) return;
        const height = `${Math.round(width / this.slideRatio(active))}px`;
        if (this.viewport.style.height !== height) this.viewport.style.height = height;
      }

      updateArrows() {
        if (!this.embla) return;
        if (this.prevArrow) this.prevArrow.toggleAttribute('disabled', !this.embla.canScrollPrev());
        if (this.nextArrow) this.nextArrow.toggleAttribute('disabled', !this.embla.canScrollNext());
      }

      onEmblaSelect() {
        this.updateCounter();
        this.updateArrows();
        this.setViewportHeight();
        const activeMedia = this.embla.slideNodes()[this.embla.selectedScrollSnap()];
        if (activeMedia) this.activate(activeMedia);
      }

      // Set active state, play media, sync thumbnail, announce. Does NOT move the carousel.
      activate(activeMedia) {
        this.mediaList.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // Called by thumbnail clicks (prepend=false) and by product-info variant changes (prepend=true).
      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.querySelector(`.product__media-list [data-media-id="${mediaId}"]`) ||
          this.querySelector('.product__media-list [data-media-id]');
        if (!activeMedia) return;

        if (prepend) {
          // Variant change: the featured media jumps to the front; the <ul> may have been
          // re-ordered/added-to by product-info.js, so Embla must re-read the DOM.
          if (activeMedia.parentElement.firstChild !== activeMedia) activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.embla) this.embla.reInit();
        }

        if (this.embla) {
          const index = Array.prototype.indexOf.call(this.embla.slideNodes(), activeMedia);
          if (index < 0 || index === this.embla.selectedScrollSnap()) {
            // No carousel move needed (already selected) — sync state directly.
            this.activate(activeMedia);
          } else {
            // scrollTo(index, jump): jump instantly on variant change, animate on thumbnail click.
            this.embla.scrollTo(index, Boolean(prepend));
          }
        } else {
          // No-Embla fallback: original native scroll-snap behavior.
          this.activate(activeMedia);
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
          });
        }

        // Keep Dawn's behavior: if the gallery has scrolled above the viewport, bring it back.
        window.setTimeout(() => {
          const rect = activeMedia.getBoundingClientRect();
          if (rect.top > -0.5) return;
          window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' });
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (typeof this.elements.thumbnails.isSlideVisible === 'function') {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.mediaList) return;
        this.mediaList.setAttribute('role', 'presentation');
        this.mediaList.querySelectorAll(':scope > li').forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
